import { Box, Stack } from '@mui/material';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import SearchFilterDrawer from '../search-toolbar/search-filter-drawer.component';
import { useSearchParamsContext } from '../search.hooks';
import DateFilter from './date-filter/date-filter.component';
import ArrayFilterChip from './filter-chip/array-filter-chip.component';
import DateRangeFilterChip from './filter-chip/date-range-filter-chip.component';
import FilterChip from './filter-chip/filter-chip.component';
import MultiSelectFilter from './multi-select-filter/multi-select-filter.component';
import SearchFilterButtonDropdown from './search-filter-button-dropdown/search-filter-button-dropdown.component';
import SingleSelectFilter from './single-select-filter/single-select-filter.component';
import TextInputFilter from './text-input-filter/text-input-filter.component';
import ComparisonDateRangeFilter from './date-filter/comparison-date-range-filter.component';

export type SearchFiltersPropsType = {
  excludedFilters?: string[];
};

const SearchFilters = ({ excludedFilters }: SearchFiltersPropsType) => {
  const { searchFilters, resetParam, resetAllParams } =
    useSearchParamsContext();
  const { open, close, toggleWithData, toggle, drawerData } =
    useUtilityDrawer();

  const filteredSearchFilters = Object.keys(searchFilters).reduce(
    (acc, key) => {
      if (!excludedFilters.includes(key)) {
        acc[key] = searchFilters[key];
      }
      return acc;
    },
    {} as typeof searchFilters,
  );

  return (
    <Box>
      <Stack
        spacing={1}
        direction="row"
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        data-testid="SearchFilters"
      >
        <>
          <SearchFilterButtonDropdown toggle={toggle} reset={resetAllParams} />
          {Object.values(filteredSearchFilters).map(searchFilter => {
            /* istanbul ignore next */
            switch (searchFilter.filter.type) {
              case 'MultiSelectFilter':
                const value = searchFilter.value as string[];
                const labels = searchFilter.options
                  .filter(opt => value.includes(opt.id))
                  .map(opt => opt.label);
                return (
                  <ArrayFilterChip
                    openFilterDrawer={() =>
                      toggleWithData(searchFilter.paramName)
                    }
                    name={searchFilter.filter.label}
                    value={labels}
                    reset={() => resetParam(searchFilter.paramName)}
                    active={
                      searchFilter.toUrlString(searchFilter.value) !==
                      searchFilter.toUrlString(searchFilter.defaultValue)
                    }
                    key={searchFilter.paramName}
                  />
                );
              case 'SingleSelectFilter':
                return (
                  <FilterChip
                    openFilterDrawer={() =>
                      toggleWithData(searchFilter.paramName)
                    }
                    name={searchFilter.filter.label}
                    value={
                      searchFilter.options.find(
                        opt => opt.id === searchFilter.value,
                      )?.label
                    }
                    reset={() => resetParam(searchFilter.paramName)}
                    active={
                      searchFilter.toUrlString(searchFilter.value) !==
                      searchFilter.toUrlString(searchFilter.defaultValue)
                    }
                    key={searchFilter.paramName}
                  />
                );
              case 'DateRangeFilter':
              case 'ComparisonDateRangeFilter':
                return (
                  <DateRangeFilterChip
                    openFilterDrawer={() =>
                      toggleWithData(searchFilter.paramName)
                    }
                    name={searchFilter.filter.label}
                    startParamName={searchFilter.filter.startParamName}
                    endParamName={searchFilter.filter.endParamName}
                    key={searchFilter.paramName}
                    showName={searchFilter.filter.showName}
                  />
                );
              case 'TextInputFilter':
                return (
                  <FilterChip
                    openFilterDrawer={() =>
                      toggleWithData(searchFilter.paramName)
                    }
                    name={searchFilter.filter.label}
                    value={searchFilter.value as string}
                    reset={() => resetParam(searchFilter.paramName)}
                    active={
                      searchFilter.toUrlString(searchFilter.value) !==
                      searchFilter.toUrlString(searchFilter.defaultValue)
                    }
                    key={searchFilter.paramName}
                  />
                );
              default:
                /* istanbul ignore next */
                return null;
            }
          })}
        </>
      </Stack>
      <SearchFilterDrawer open={open} close={close} drawerData={drawerData}>
        {Object.values(filteredSearchFilters).map(searchFilter => {
          /* istanbul ignore next */
          switch (searchFilter.filter.type) {
            case 'MultiSelectFilter':
              return (
                <MultiSelectFilter
                  searchParamName={searchFilter.paramName}
                  key={searchFilter.paramName}
                  data-testid={searchFilter['data-testid']}
                />
              );
            case 'SingleSelectFilter':
              return (
                <SingleSelectFilter
                  searchParamName={searchFilter.paramName}
                  key={searchFilter.paramName}
                />
              );
            case 'DateRangeFilter':
              return (
                <DateFilter
                  startParamName={searchFilter.filter.startParamName}
                  endParamName={searchFilter.filter.endParamName}
                  predefinedRangeOptions={
                    searchFilter.filter.predefinedDateOptions
                  }
                  presetsOnly={searchFilter.filter.presetsOnly}
                  key={searchFilter.paramName}
                  label={
                    searchFilter.filter.showName
                      ? searchFilter.filter.label
                      : undefined
                  }
                  maxDate={searchFilter.filter.maxDate}
                  anchorDate={searchFilter.filter.anchorDate}
                />
              );
            case 'ComparisonDateRangeFilter':
              return (
                <ComparisonDateRangeFilter
                  startParamName={searchFilter.filter.startParamName}
                  endParamName={searchFilter.filter.endParamName}
                  compareStartParamName={
                    searchFilter.filter.compareStartParamName
                  }
                  compareEndParamName={searchFilter.filter.compareEndParamName}
                  key={searchFilter.paramName}
                  label={
                    searchFilter.filter.showName
                      ? searchFilter.filter.label
                      : undefined
                  }
                />
              );
            case 'TextInputFilter':
              return (
                <TextInputFilter
                  searchParamName={searchFilter.paramName}
                  key={searchFilter.paramName}
                />
              );
            default:
              /* istanbul ignore next */
              return null;
          }
        })}
      </SearchFilterDrawer>
    </Box>
  );
};

export default SearchFilters;
